import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import RefreshIcon from "@material-ui/icons/Refresh";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
    // O contentWrapper do MainContainer corta o que passa da tela. Sem rolagem
    // propria, tudo daqui para baixo fica inalcancavel.
    flex: 1,
    minHeight: 0,
    overflowY: "auto"
  },
  cartao: {
    padding: theme.spacing(2.5),
    borderRadius: 12,
    height: "100%"
  },
  secao: { padding: theme.spacing(3), borderRadius: 12, marginTop: 20 },
  caixaLink: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap"
  }
}));

const moeda = v =>
  `R$ ${Number(v || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const data = d =>
  d
    ? new Date(d).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      })
    : "—";

const Afiliados = () => {
  const classes = useStyles();
  const history = useHistory();

  const [carregando, setCarregando] = useState(true);
  const [afiliado, setAfiliado] = useState(null);
  const [vendas, setVendas] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [links, setLinks] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [apelido, setApelido] = useState("");
  const [emailPagamento, setEmailPagamento] = useState("");

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data: info } = await api.get("/affiliate/my-info");

      // Sem liberação não há painel. Quem cai aqui pela URL volta para o começo.
      if (!info?.enabled) {
        toast.info("A sua afiliação ainda não foi liberada.");
        history.push("/");
        return;
      }
      setAfiliado(info);
      setApelido(info.slug || "");
      // Sem e-mail proprio informado, comeca com o da conta — que e o caso
      // mais comum.
      setEmailPagamento(info.paytEmail || info.company?.email || "");

      const [vendasResp, planosResp, linksResp] = await Promise.all([
        api.get("/affiliate/commissions").catch(() => ({ data: [] })),
        api.get("/plans/list").catch(() => ({ data: [] })),
        api.get("/affiliate/my-links").catch(() => ({ data: [] }))
      ]);

      const listaVendas = Array.isArray(vendasResp.data)
        ? vendasResp.data
        : vendasResp.data?.commissions || [];
      setVendas(listaVendas);

      const listaPlanos = Array.isArray(planosResp.data) ? planosResp.data : [];
      setPlanos(listaPlanos.filter(p => p.isPublic !== false));

      const guardados = {};
      (Array.isArray(linksResp.data) ? linksResp.data : []).forEach(l => {
        if (l.planId) guardados[l.planId] = l.url;
      });
      setLinks(guardados);
    } catch (e) {
      toast.error("Não consegui carregar o seu painel.");
    } finally {
      setCarregando(false);
    }
  }, [history]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const copiar = texto => {
    navigator.clipboard.writeText(texto);
    toast.success("Copiado!");
  };

  const salvarLinks = async () => {
    setSalvando(true);
    try {
      // O nome da pagina vem antes: sem ele os links nao tem onde aparecer.
      if (apelido && apelido !== afiliado?.slug) {
        await api.post("/affiliate/slug", { slug: apelido });
      }
      if (emailPagamento !== (afiliado?.paytEmail || "")) {
        await api.post("/affiliate/meu-email", { paytEmail: emailPagamento });
      }
      await api.post("/affiliate/checkout-links", { links });
      toast.success("Tudo salvo.");
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Não consegui salvar.");
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) {
    return (
      <MainContainer>
        <Box display="flex" justifyContent="center" mt={8}>
          <CircularProgress />
        </Box>
      </MainContainer>
    );
  }

  if (!afiliado) return null;

  const totalVendas = vendas.length;
  const totalGanho = vendas.reduce(
    (s, v) => s + Number(v.commissionAmount || 0),
    0
  );
  // Divulga o dominio curto; o com "app." tambem abre.
  const dominioCurto = window.location.origin.replace("//app.", "//");
  const paginaPropria = afiliado.slug ? `${dominioCurto}/p/${afiliado.slug}` : "";

  return (
    <MainContainer>
      <Container maxWidth="lg" className={classes.container}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          mb={1}
        >
          <Box>
            <Typography variant="h4" style={{ fontWeight: 700 }}>
              Painel do Afiliado
            </Typography>
            <Typography variant="body2" style={{ color: "#64748b" }}>
              Acompanhe as suas vendas e divulgue os seus links.
            </Typography>
          </Box>
          <Button startIcon={<RefreshIcon />} onClick={carregar}>
            Atualizar
          </Button>
        </Box>

        {/* Resumo */}
        <Box display="flex" gridGap={16} flexWrap="wrap" mt={2}>
          <Paper className={classes.cartao} style={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h4" style={{ fontWeight: 700 }}>
              {moeda(totalGanho)}
            </Typography>
            <Typography variant="body2" style={{ color: "#64748b" }}>
              Comissão acumulada
            </Typography>
          </Paper>
          <Paper className={classes.cartao} style={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h4" style={{ fontWeight: 700 }}>
              {totalVendas}
            </Typography>
            <Typography variant="body2" style={{ color: "#64748b" }}>
              {totalVendas === 1 ? "Venda" : "Vendas"}
            </Typography>
          </Paper>
        </Box>

        {/* Passo 1: afiliar-se */}
        {afiliado.paytAffiliateUrl && (
          <Paper className={classes.secao}>
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              1. Cadastre-se como afiliado
            </Typography>
            <Typography
              variant="body2"
              style={{ color: "#64748b", marginBottom: 14 }}
            >
              O pagamento da sua comissão é feito pela plataforma de pagamento.
              Cadastre-se lá para receber.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              endIcon={<OpenInNewIcon />}
              href={afiliado.paytAffiliateUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Fazer meu cadastro de afiliado
            </Button>
          </Paper>
        )}

        {/* Passo 2: colar os links */}
        <Paper className={classes.secao}>
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            2. Cole os seus links e escolha o nome da sua página
          </Typography>
          <Typography
            variant="body2"
            style={{ color: "#64748b", marginBottom: 6 }}
          >
            Depois de se cadastrar, copie lá o seu link de cada plano e cole
            aqui.
          </Typography>
          <Box
            p={1.5}
            mb={2}
            bgcolor="#fffbeb"
            border="1px solid #f59e0b"
            borderRadius={8}
          >
            <Typography variant="body2" style={{ color: "#92400e" }}>
              <strong>Confira antes de salvar.</strong> Se o link estiver errado
              ou for de outra pessoa, a venda acontece mas a comissão vai para
              quem o link indicar — e não temos como corrigir depois.
            </Typography>
          </Box>

          {planos.map(p => (
            <Box key={p.id} mb={2}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label={`${p.name} — ${moeda(p.amount)}`}
                placeholder="Cole aqui o seu link deste plano"
                value={links[p.id] || ""}
                onChange={e =>
                  setLinks({ ...links, [p.id]: e.target.value.trim() })
                }
              />
            </Box>
          ))}

          <Box mt={1} mb={2}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Seu e-mail na plataforma de pagamento"
              placeholder="voce@email.com"
              value={emailPagamento}
              onChange={e => setEmailPagamento(e.target.value.trim())}
              helperText="É por ele que as suas vendas são identificadas. Se você usa outro e-mail lá, corrija aqui."
            />
          </Box>

          <Box mt={1} mb={2}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="O nome da sua página"
              placeholder="seunome"
              value={apelido}
              onChange={e =>
                setApelido(
                  e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")
                )
              }
              helperText={
                apelido
                  ? `A sua página vai ficar em ${window.location.origin.replace(
                      "//app.",
                      "//"
                    )}/p/${apelido}`
                  : "Só letras, números e hífen. É o que aparece no seu endereço."
              }
            />
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={salvarLinks}
            disabled={salvando}
          >
            {salvando ? "Salvando..." : "Salvar"}
          </Button>
        </Paper>

        {/* Passo 3: a página dele */}
        {paginaPropria && (
          <Paper className={classes.secao}>
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              3. A sua página
            </Typography>
            <Typography
              variant="body2"
              style={{ color: "#64748b", marginBottom: 14 }}
            >
              Divulgue este endereço. Quem se cadastrar por ele vai para os seus
              links, e a venda conta para você.
            </Typography>
            <Box className={classes.caixaLink}>
              <TextField
                variant="outlined"
                size="small"
                value={paginaPropria}
                InputProps={{ readOnly: true }}
                style={{ flex: 1, minWidth: 260 }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileCopyIcon />}
                onClick={() => copiar(paginaPropria)}
              >
                Copiar
              </Button>
            </Box>
          </Paper>
        )}

        {/* Vendas */}
        <Paper className={classes.secao}>
          <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 12 }}>
            As suas vendas
          </Typography>

          {vendas.length === 0 ? (
            <Typography variant="body2" style={{ color: "#94a3b8" }}>
              Nenhuma venda ainda. Assim que alguém comprar pelo seu link, ela
              aparece aqui.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Plano</TableCell>
                    <TableCell align="right">Venda</TableCell>
                    <TableCell align="right">Sua comissão</TableCell>
                    <TableCell>Situação</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vendas.map(v => (
                    <TableRow key={v.id}>
                      <TableCell>{data(v.createdAt)}</TableCell>
                      <TableCell>
                        {v.metadata?.compradorNome || "—"}
                      </TableCell>
                      <TableCell>{v.metadata?.plano || "—"}</TableCell>
                      <TableCell align="right">
                        {moeda(v.metadata?.valorVenda)}
                      </TableCell>
                      <TableCell align="right" style={{ fontWeight: 600 }}>
                        {moeda(v.commissionAmount)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={v.status === "paid" ? "Confirmada" : v.status}
                          style={{
                            backgroundColor:
                              v.status === "paid" ? "#10b981" : "#94a3b8",
                            color: "#fff"
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>
    </MainContainer>
  );
};

export default Afiliados;
